import React, { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import { motion, Variants, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";

const renderInlineBold = (text: string) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    const match = part.match(/^\*\*(.+)\*\*$/);
    return match ? (
      <strong key={i} style={{ fontWeight: 700, color: "#1D2F41" }}>
        {match[1]}
      </strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    );
  });

const ease = [0.16, 0.77, 0.47, 0.97] as const;

const viewport = { once: true, amount: 0.15 };

const SPINE_X = 20;

const cardReveal = (i: number): Variants => ({
  hidden: { opacity: 0, x: -12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease, delay: 0.3 + i * 0.18 + 0.2 },
  },
});

const labelReveal: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
};

const TreeRow: React.FC<{
  item: {
    number: string;
    title: string;
    description: string;
    conclusion: string;
  };
  index: number;
  isLast: boolean;
  inView: boolean;
  spineTopOffset?: number;
  spineRef?: React.RefObject<HTMLDivElement>;
}> = ({ item, index, isLast, inView, spineTopOffset = 0, spineRef }) => {
  const isRight = index === 1; // Middle item has spine on the right

  return (
    <Box
      sx={{ display: "flex", position: "relative", minHeight: { md: "180px" } }}
    >
      {/* Vertical spine segment for this row */}
      <Box
        ref={spineRef}
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: `${spineTopOffset}px`,
          bottom: 0,
          left: isRight ? undefined : `${SPINE_X}px`,
          right: isRight ? `${SPINE_X}px` : undefined,
          width: "1px",
        }}
      >
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{
            hidden: { scaleY: 0 },
            visible: {
              scaleY: 1,
              transition: { duration: 0.6, ease, delay: index * 0.2 },
            },
          }}
          style={{
            width: "1px",
            height: "100%",
            background:
              "repeating-linear-gradient(to bottom, rgba(29,47,65,0.2) 0px, rgba(29,47,65,0.2) 4px, transparent 4px, transparent 8px)",
            transformOrigin: "top",
          }}
        />
      </Box>

      {/* Card content */}
      <motion.div
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={cardReveal(index)}
        style={{ flex: 1 }}
      >
        <Box
          sx={{
            py: { xs: 4, md: 4 },
            pl: { md: 6 },
            pr: { md: 6 }, // Add padding on right to avoid right spine
            position: "relative",
            "&::after": isLast
              ? undefined
              : {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: { xs: 0, md: `${SPINE_X}px` }, // Connects left spine
                  right: { xs: 0, md: `${SPINE_X}px` }, // Connects right spine
                  height: "1px",
                  background:
                    "repeating-linear-gradient(to right, rgba(29,47,65,0.2) 0px, rgba(29,47,65,0.2) 4px, transparent 4px, transparent 8px)",
                },
          }}
        >
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(29,47,65,0.3)",
              fontFamily: '"Calibri", sans-serif',
              mb: 1,
            }}
          >
            {item.number}
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1.6rem", md: "2rem", lg: "2.3rem" },
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
              color: "#1D2F41",
              fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif',
              mb: { xs: 2, md: 2.5 },
            }}
          >
            {item.title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "0.95rem", md: "1rem" },
              fontWeight: 300,
              lineHeight: 1.8,
              color: "rgba(29,47,65,0.5)",
              fontFamily: '"Calibri", sans-serif',
              maxWidth: "600px",
              mb: 2,
            }}
          >
            {renderInlineBold(item.description)}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#1D2F41",
              fontFamily: '"Calibri", sans-serif',
            }}
          >
            {item.conclusion}
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
};

/* Section*/
const Services: React.FC = () => {
  const { t } = useTranslation();
  const label = t("services.label");
  const items = t("services.items", { returnObjects: true }) as Array<{
    number: string;
    title: string;
    description: string;
    conclusion: string;
  }>;

  const treeRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const labelRef = React.useRef<HTMLDivElement>(null);
  const spineRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(treeRef, { once: true, amount: 0.15 });

  const [connector, setConnector] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [spineTop, setSpineTop] = React.useState(0);

  React.useEffect(() => {
    const measure = () => {
      const c = containerRef.current;
      const l = labelRef.current;
      const s = spineRef.current;
      const t = treeRef.current;
      if (!c || !l || !s || !t) return;
      const cr = c.getBoundingClientRect();
      const lr = l.getBoundingClientRect();
      const sr = s.getBoundingClientRect();
      const tr = t.getBoundingClientRect();
      const connectorY = lr.bottom - cr.top;
      setConnector({
        top: connectorY,
        left: lr.right - cr.left,
        width: sr.left - lr.right,
      });
      // Spine top relative to the tree container
      setSpineTop(connectorY - (tr.top - cr.top));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#f8f9fb",
        position: "relative",
        isolation: "isolate",
      }}
    >
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: 0,
          left: 0,
          width: "calc(max(0px, (100% - 1280px) / 2) + 258px)",
          height: "1px",
          bgcolor: "rgba(29,47,65,0.15)",
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: 0,
          left: "calc(max(0px, (100% - 1280px) / 2) + 358px)",
          right: 0,
          height: "1px",
          bgcolor: "rgba(29,47,65,0.15)",
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          bgcolor: "rgba(29,47,65,0.15)",
          zIndex: 2,
        }}
      />

      {/* Bottom border */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "calc(max(0px, (100% - 1280px) / 2) + 258px)",
          height: "1px",
          bgcolor: "rgba(29,47,65,0.15)",
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          bottom: 0,
          left: "calc(max(0px, (100% - 1280px) / 2) + 358px)",
          right: 0,
          height: "1px",
          bgcolor: "rgba(29,47,65,0.15)",
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "1px",
          bgcolor: "rgba(29,47,65,0.15)",
          zIndex: 2,
        }}
      />

      {/* Vertical line (decorative, from section above) */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: 0,
          left: "calc(max(0px, (100% - 1280px) / 2) + 308px)",
          width: "1px",
          height: "100%",
          // Use a linear gradient to create a 100px gap around the horizontal shredded line (connector.top)
          background:
            connector.top > 0
              ? `linear-gradient(to bottom, rgba(29,47,65,0.15) 0px, rgba(29,47,65,0.15) calc(${connector.top}px - 50px), transparent calc(${connector.top}px - 50px), transparent calc(${connector.top}px + 50px), rgba(29,47,65,0.15) calc(${connector.top}px + 50px), rgba(29,47,65,0.15) 100%)`
              : "rgba(29,47,65,0.15)",
          zIndex: 2,
        }}
      />

      <Box
        ref={containerRef}
        sx={{
          maxWidth: 1280,
          mx: "auto",
          position: "relative",
          zIndex: 1,
          px: { xs: 3, md: 6 },
          py: { xs: 8, md: 12 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 6, md: 0 },
        }}
      >
        {connector.width > 0 && (
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              top: `${connector.top}px`,
              left: `${connector.left}px`,
              width: `${connector.width}px`,
              height: "1px",
              zIndex: 3,
            }}
          >
            <motion.div
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              variants={{
                hidden: { scaleX: 0 },
                visible: {
                  scaleX: 1,
                  transition: { duration: 0.35, ease, delay: 0.05 },
                },
              }}
              style={{
                width: "100%",
                height: "1px",
                background:
                  "repeating-linear-gradient(to right, rgba(29,47,65,0.2) 0px, rgba(29,47,65,0.2) 4px, transparent 4px, transparent 8px)",
                transformOrigin: "left",
              }}
            />
          </Box>
        )}

        <Box sx={{ flexShrink: 0, width: { md: "260px" }, pr: { md: 6 } }}>
          <Box>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={labelReveal}
            >
              <Typography
                ref={labelRef}
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "rgba(29,47,65,0.45)",
                  fontFamily: '"Calibri", sans-serif',
                  lineHeight: 1.6,
                  width: "fit-content",
                  pt: "10px",
                  pl: "10px",
                  pb: "10px",
                }}
              >
                {label.split("\n").map((line: string, i: number) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </Typography>
            </motion.div>
          </Box>
        </Box>

        <Box
          ref={treeRef}
          sx={{ flex: 1, pl: { md: 8 }, position: "relative" }}
        >
          {/* Cards with spine segments and branches */}
          {items.map((item, i) => (
            <TreeRow
              key={item.number}
              item={item}
              index={i}
              isLast={i === items.length - 1}
              inView={inView}
              spineTopOffset={i === 0 ? spineTop : 0}
              spineRef={i === 0 ? spineRef : undefined}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Services;
